import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type {
    ApiSpinTogetherFriendStateAny,
    ApiSpinTogetherPrize,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import beachCoinIcon from "../../../../../assets/static/images/symbol-beach-coins.webp";
import gramIcon from "../../../../../assets/static/images/coins/style1/coin-style1__gram.webp";
import notcoinIcon from "../../../../../assets/static/images/coins/style1/coin-style1__not.webp";
import usdtIcon from "../../../../../assets/static/images/coins/style1/coin-style1__usdt.webp";
import energyIcon from "../../../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import friendStealIcon from "../../../../../assets/static/images/symbols/v1/symbol-friend-steal.webp";
import { formatCompactCurrencyMinorUnits } from "../../../../../logic/helpers/currencyAmount";
import { spinTogetherBenefitSummary } from "../../../../../logic/helpers/spinTogetherBenefits";
import {
    SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS,
    SPIN_TOGETHER_DEFAULT_OUTCOME_DISPLAY_SECONDS,
    spinTogetherActivityIsFresh,
    spinTogetherLiveActivityIsFresh,
    spinTogetherOutcomePresentationIsFresh,
    type SpinTogetherOutcomePresentation,
} from "../../../../../logic/helpers/spinTogetherActivity";
import { shareUrlViaTelegram } from "../../../../../logic/helpers/telegram";
import { useFriendInviteSharing } from "../../../../../logic/hooks/useSocialFriendInvites";
import type { SpinTogetherController } from "../../../../../logic/hooks/useSpinTogether";
import { TELEGRAM_MINI_APP_URL } from "../../../../../state/config";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../../../common/SimpleDialog/comps/SimpleDialogHeader";
import { SpinTogetherBenefits } from "./components/SpinTogetherBenefits/SpinTogetherBenefits";
import "./FriendsRail.scss";
import whiteOutlined3DPlusIcon from "../../../../../assets/static/images/spin-together/white-outlines-3d-plus.webp";

const CURRENCY_ICONS: Readonly<Record<string, string>> = {
    BEACH_COINS: beachCoinIcon,
    GRAM: gramIcon,
    NOT: notcoinIcon,
    USDT: usdtIcon,
};

type StatusVariant = "offline" | "online" | "busy" | "spinning" | "outcome";

interface StatusView {
    variant: StatusVariant;
    label: string | null;
    prizes: StatusPrizeView[];
}

interface StatusPrizeView {
    key: string;
    label: string;
    icon: string | null;
}

function prizeView(
    prize: ApiSpinTogetherPrize,
    locale: string,
    index: number,
): StatusPrizeView {
    switch (prize.kind) {
        case "CURRENCY":
            return {
                key: `currency:${prize.currencyCode}:${index}`,
                label: `+${formatCompactCurrencyMinorUnits(
                    prize.amount,
                    prize.currencyCode,
                    locale,
                )}`,
                icon: CURRENCY_ICONS[prize.currencyCode] ?? null,
            };
        case "ENERGY":
            return {
                key: `energy:${index}`,
                label: `+${prize.amount}`,
                icon: energyIcon,
            };
        case "INVENTORY":
            return {
                key: `inventory:${prize.itemCode}:${index}`,
                label: `+${prize.quantity}`,
                icon:
                    prize.itemCode === "FRIEND_STEAL" ? friendStealIcon : null,
            };
    }
}

function statusView(
    friend: ApiSpinTogetherFriendStateAny,
    outcomePresentation: SpinTogetherOutcomePresentation | undefined,
    t: TFunction,
    locale: string,
    nowMs: number,
    outcomeDisplaySeconds: number,
): StatusView {
    const settledOutcome = spinTogetherOutcomePresentationIsFresh(
        outcomePresentation,
        nowMs,
        outcomeDisplaySeconds,
    )
        ? outcomePresentation?.outcome
        : null;
    if (settledOutcome?.result === "WIN" && settledOutcome.prizes.length > 0) {
        return {
            variant: "outcome",
            label: null,
            prizes: settledOutcome.prizes.map((prize, index) =>
                prizeView(prize, locale, index),
            ),
        };
    }
    const transientActivity = spinTogetherActivityIsFresh(
        friend.activity,
        nowMs,
    )
        ? friend.activity
        : null;
    if (
        transientActivity?.state === "WIN" &&
        transientActivity.winnings.length > 0
    ) {
        return {
            variant: "outcome",
            label: null,
            prizes: transientActivity.winnings.map((winning, index) =>
                prizeView({ kind: "CURRENCY", ...winning }, locale, index),
            ),
        };
    }
    if (friend.presence === "OFFLINE") {
        return {
            variant: "offline",
            label: t("friendsRail.status.offline"),
            prizes: [],
        };
    }
    const liveActivity = spinTogetherLiveActivityIsFresh(
        friend.liveActivity,
        nowMs,
    )
        ? friend.liveActivity
        : transientActivity?.state === "SPINNING"
          ? transientActivity
          : null;
    if (liveActivity?.state === "SPINNING") {
        return {
            variant: "spinning",
            label: t("friendsRail.status.spinning"),
            prizes: [],
        };
    }
    if (friend.presence === "BUSY") {
        return {
            variant: "busy",
            label: t("spinTogether.busy", { defaultValue: "Busy" }),
            prizes: [],
        };
    }
    return {
        variant: "online",
        label:
            friend.session && Date.parse(friend.session.expiresAt) > nowMs
                ? t("spinTogether.together", { defaultValue: "Together" })
                : t("friendsRail.status.active"),
        prizes: [],
    };
}

function friendNameParts(friend: ApiSpinTogetherFriendStateAny): string[] {
    const names = [friend.user.firstName, friend.user.lastName].filter(
        (value): value is string => Boolean(value?.trim()),
    );
    return names.length > 0 ? names : [friend.user.displayName];
}

function initials(friend: ApiSpinTogetherFriendStateAny): string {
    return friendNameParts(friend)
        .slice(0, 2)
        .map((part) => part.trim().slice(0, 1))
        .join("")
        .toUpperCase();
}

export interface FriendsRailProps {
    spinTogether: SpinTogetherController;
    className?: string;
}

export default function FriendsRail({
    spinTogether,
    className,
}: FriendsRailProps) {
    const { t, i18n } = useTranslation();
    const { state: authState } = useSlice("authSlice");
    const isAuthReady = authState.bootstrapStatus === "ready";
    const friendInviteSharing = useFriendInviteSharing({
        enabled: isAuthReady,
        userId: authState.userId,
        preferredTransport: "share-link",
    });
    const listRef = useRef<HTMLUListElement>(null);
    const [selectedFriendId, setSelectedFriendId] = useState<string | null>(
        null,
    );
    const [inviteStatus, setInviteStatus] = useState<
        "idle" | "sending" | "sent" | "error"
    >("idle");
    const [statusRevision, setStatusRevision] = useState(0);
    const [scrollOverflow, setScrollOverflow] = useState({
        left: false,
        right: false,
    });
    const statusNowMs = Date.now();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const selectedFriend =
        spinTogether.friends.find(
            (friend) => friend.user.userId === selectedFriendId,
        ) ?? null;
    const selectedFriendBenefits = spinTogetherBenefitSummary(
        selectedFriend,
        spinTogether.config,
        spinTogether.friends,
        spinTogether.recentRewards,
        statusNowMs,
    );
    const selectedSessionIsActive =
        selectedFriend?.session !== null &&
        selectedFriend?.session !== undefined &&
        Date.parse(selectedFriend.session.expiresAt) > statusNowMs;
    const outcomeDisplaySeconds =
        spinTogether.config?.outcomePresentation?.displaySeconds ??
        SPIN_TOGETHER_DEFAULT_OUTCOME_DISPLAY_SECONDS;
    const outcomePresentationByFriendId = new Map<
        string,
        SpinTogetherOutcomePresentation
    >();
    for (const presentation of spinTogether.outcomePresentations) {
        if (
            !outcomePresentationByFriendId.has(presentation.friendUserId) &&
            spinTogetherOutcomePresentationIsFresh(
                presentation,
                statusNowMs,
                outcomeDisplaySeconds,
            )
        ) {
            outcomePresentationByFriendId.set(
                presentation.friendUserId,
                presentation,
            );
        }
    }

    useEffect(() => {
        const nowMs = Date.now();
        let nextExpiryMs = Number.POSITIVE_INFINITY;
        for (const friend of spinTogether.friends) {
            const expiries: number[] = [];
            if (
                friend.activity?.state === "SPINNING" ||
                friend.activity?.state === "WIN"
            ) {
                expiries.push(
                    Date.parse(friend.activity.occurredAt) +
                        SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS,
                );
            }
            if (friend.liveActivity?.state === "SPINNING") {
                expiries.push(
                    Date.parse(friend.liveActivity.occurredAt) +
                        SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS,
                );
            }
            if (friend.session) {
                expiries.push(Date.parse(friend.session.expiresAt));
                if (friend.session.boostEndsAt) {
                    expiries.push(Date.parse(friend.session.boostEndsAt));
                }
            }
            for (const expiresAtMs of expiries) {
                if (
                    Number.isFinite(expiresAtMs) &&
                    expiresAtMs > nowMs &&
                    expiresAtMs < nextExpiryMs
                ) {
                    nextExpiryMs = expiresAtMs;
                }
            }
        }
        for (const presentation of spinTogether.outcomePresentations) {
            const expiresAtMs =
                presentation.receivedAtMs + outcomeDisplaySeconds * 1_000;
            if (
                Number.isFinite(expiresAtMs) &&
                expiresAtMs > nowMs &&
                expiresAtMs < nextExpiryMs
            ) {
                nextExpiryMs = expiresAtMs;
            }
        }
        if (!Number.isFinite(nextExpiryMs)) return;
        const timer = window.setTimeout(
            () => setStatusRevision((current) => current + 1),
            nextExpiryMs - nowMs + 25,
        );
        return () => window.clearTimeout(timer);
    }, [
        outcomeDisplaySeconds,
        spinTogether.friends,
        spinTogether.outcomePresentations,
        statusRevision,
    ]);

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const updateScrollOverflow = () => {
            const next = {
                left: list.scrollLeft > 1,
                right:
                    list.scrollLeft + list.clientWidth < list.scrollWidth - 1,
            };
            setScrollOverflow((current) =>
                current.left === next.left && current.right === next.right
                    ? current
                    : next,
            );
        };

        updateScrollOverflow();
        list.addEventListener("scroll", updateScrollOverflow, {
            passive: true,
        });

        const resizeObserver = new ResizeObserver(updateScrollOverflow);
        resizeObserver.observe(list);
        for (const item of list.children) {
            resizeObserver.observe(item);
        }

        return () => {
            list.removeEventListener("scroll", updateScrollOverflow);
            resizeObserver.disconnect();
        };
    }, [spinTogether.friends.length]);

    const shareApp = () => {
        shareUrlViaTelegram(
            TELEGRAM_MINI_APP_URL,
            t("spinTogether.shareMessage", {
                defaultValue: "Join me on Pebbler — let’s play together!",
            }),
        );
    };

    const sendLiveInvite = async () => {
        if (!selectedFriend) return;
        setInviteStatus("sending");
        try {
            await spinTogether.invite(selectedFriend.user.userId);
            setInviteStatus("sent");
        } catch {
            setInviteStatus("error");
        }
    };

    return (
        <>
            <div
                className={["friends-rail", className]
                    .filter(Boolean)
                    .join(" ")}
                dir="ltr"
            >
                {/* <button
                    type="button"
                    className="friends-rail__arrow friends-rail__arrow--left"
                    aria-label={t("friendsRail.scrollLeft")}
                    onClick={() => scrollByStep(-1)}
                >
                    <img
                        className="friends-rail__arrow-icon"
                        src={whiteArrowLeftIcon}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                </button> */}

                <ul
                    className="friends-rail__list"
                    ref={listRef}
                    data-overflow-left={scrollOverflow.left}
                    data-overflow-right={scrollOverflow.right}
                >
                    {spinTogether.friends.map((friend) => {
                        const status = statusView(
                            friend,
                            outcomePresentationByFriendId.get(
                                friend.user.userId,
                            ),
                            t,
                            locale,
                            statusNowMs,
                            outcomeDisplaySeconds,
                        );
                        return (
                            <li
                                key={friend.user.userId}
                                className={`friends-rail__item friends-rail__item--${status.variant}`}
                            >
                                <SoundButton
                                    type="button"
                                    className="friends-rail__friend"
                                    aria-label={t("spinTogether.openFriend", {
                                        name: friend.user.displayName,
                                        defaultValue:
                                            "Open {{name}}’s play-together options",
                                    })}
                                    onClick={() => {
                                        setInviteStatus("idle");
                                        setSelectedFriendId(friend.user.userId);
                                    }}
                                >
                                    <span className="friends-rail__avatar">
                                        <span className="friends-rail__initials">
                                            {initials(friend)}
                                        </span>
                                        {friend.user.avatarUrl && (
                                            <img
                                                className="friends-rail__avatar__photo"
                                                src={friend.user.avatarUrl}
                                                alt=""
                                                draggable={false}
                                                loading="lazy"
                                                onError={(event) => {
                                                    event.currentTarget.style.display =
                                                        "none";
                                                }}
                                            />
                                        )}
                                        {friend.presence === "ONLINE" && (
                                            <span
                                                className="friends-rail__online-indicator"
                                                aria-hidden="true"
                                            />
                                        )}
                                        <span className="friends-rail__status">
                                            {status.label !== null ? (
                                                <span className="friends-rail__status__label">
                                                    {status.label}
                                                </span>
                                            ) : (
                                                <span className="friends-rail__status__prizes">
                                                    {status.prizes.map(
                                                        (prize) => (
                                                            <span
                                                                className="friends-rail__status__prize"
                                                                key={prize.key}
                                                            >
                                                                <span className="friends-rail__status__label">
                                                                    {
                                                                        prize.label
                                                                    }
                                                                </span>
                                                                {prize.icon && (
                                                                    <img
                                                                        className="friends-rail__status__icon"
                                                                        src={
                                                                            prize.icon
                                                                        }
                                                                        alt=""
                                                                        draggable={
                                                                            false
                                                                        }
                                                                    />
                                                                )}
                                                            </span>
                                                        ),
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                    <span
                                        className="friends-rail__name"
                                        dir="auto"
                                    >
                                        {friendNameParts(friend).map(
                                            (part, index) => (
                                                <span
                                                    className="friends-rail__name__line"
                                                    dir="auto"
                                                    key={`${part}-${index}`}
                                                >
                                                    {part}
                                                </span>
                                            ),
                                        )}
                                    </span>
                                </SoundButton>
                            </li>
                        );
                    })}
                </ul>

                <div className="friends-rail__item friends-rail__item--invite">
                    <SoundButton
                        type="button"
                        className="friends-rail__invite"
                        aria-label={t("friendsRail.inviteFriends")}
                        disabled={!isAuthReady}
                        onClick={() =>
                            void friendInviteSharing.shareViaTelegramLink()
                        }
                    >
                        <img
                            className="friends-rail__invite-icon"
                            src={whiteOutlined3DPlusIcon}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                    </SoundButton>
                    <p className="friends-rail__name friends-rail__name--invite">
                        {t("friendsRail.invite")}
                    </p>
                </div>

                {/* <button
                    type="button"
                    className="friends-rail__arrow friends-rail__arrow--right"
                    aria-label={t("friendsRail.scrollRight")}
                    onClick={() => scrollByStep(1)}
                >
                    <img
                        className="friends-rail__arrow-icon"
                        src={whiteArrowLeftIcon}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                </button> */}
            </div>

            <SimpleDialog
                className="spin-together-friend-dialog"
                isOpen={selectedFriend !== null}
                onClose={() => setSelectedFriendId(null)}
                aria-labelledby="spin-together-friend-title"
                header={
                    <SimpleDialogHeader
                        title={
                            selectedFriend?.user.displayName ??
                            t("spinTogether.title", {
                                defaultValue: "Spin Together",
                            })
                        }
                        titleId="spin-together-friend-title"
                        closeLabel={t("common.close", {
                            defaultValue: "Close",
                        })}
                        onClose={() => setSelectedFriendId(null)}
                    />
                }
            >
                <div className="spin-together-friend-dialog__body">
                    <p>
                        {selectedSessionIsActive
                            ? t("spinTogether.activeSession", {
                                  defaultValue:
                                      "You’re playing together right now.",
                              })
                            : t("spinTogether.inviteDescription", {
                                  defaultValue:
                                      "Invite this friend to open Pebbler and play live.",
                              })}
                    </p>
                    {selectedSessionIsActive && selectedFriendBenefits && (
                        <SpinTogetherBenefits
                            benefits={selectedFriendBenefits}
                            locale={locale}
                        />
                    )}
                    <SoundButton
                        type="button"
                        disabled={
                            inviteStatus === "sending" ||
                            !spinTogether.preference?.allowInvites
                        }
                        onClick={() => void sendLiveInvite()}
                    >
                        {inviteStatus === "sending"
                            ? t("settings.saving")
                            : inviteStatus === "sent"
                              ? t("spinTogether.inviteSent", {
                                    defaultValue: "Invite sent",
                                })
                              : t("spinTogether.inviteLive", {
                                    defaultValue: "Invite to play live",
                                })}
                    </SoundButton>
                    <SoundButton type="button" onClick={shareApp}>
                        {t("spinTogether.shareLink", {
                            defaultValue: "Share app link",
                        })}
                    </SoundButton>
                    {inviteStatus === "error" && (
                        <p role="alert">
                            {t("spinTogether.error", {
                                defaultValue:
                                    "Spin Together is temporarily unavailable.",
                            })}
                        </p>
                    )}
                </div>
            </SimpleDialog>
        </>
    );
}

// function PlusIcon() {
//     return (
//         <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
//             <path
//                 d="M12 5v14M5 12h14"
//                 fill="none"
//                 stroke="currentColor"
//                 strokeWidth="3"
//                 strokeLinecap="round"
//             />
//         </svg>
//     );
// }
